import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { getIdToken } from '../attendedUtils';

// ── Searchable Select (inline, same as AddEvent) ──────────────────────────────
function SearchableSelect({ options, value, onChange, placeholder = 'Search...', renderLabel }) {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [focused, setFocused] = useState(false);
    const containerRef = useRef(null);

    const selected = options.find(o => String(o.id) === String(value));
    const inputValue = focused ? query : (selected ? renderLabel(selected) : query);

    const filtered = query.trim()
        ? options.filter(o => renderLabel(o).toLowerCase().includes(query.toLowerCase()))
        : options.slice(0, 50);

    useEffect(() => {
        function handleClickOutside(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false); setFocused(false); setQuery('');
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange(''); };
    const handleFocus = () => { setFocused(true); setQuery(''); setOpen(true); };
    const handleSelect = (option) => { onChange(String(option.id)); setQuery(''); setOpen(false); setFocused(false); };
    const handleClear = (e) => { e.stopPropagation(); onChange(''); setQuery(''); setOpen(false); setFocused(false); };

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    placeholder={placeholder}
                    style={styles.input}
                    autoComplete="off"
                />
                {value && (
                    <button onClick={handleClear} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#777', cursor: 'pointer', fontSize: '16px', padding: '0' }}>✕</button>
                )}
            </div>
            {open && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, backgroundColor: '#2c2c2c', border: '1px solid #444', borderTop: 'none', borderRadius: '0 0 4px 4px', maxHeight: '260px', overflowY: 'auto' }}>
                    {filtered.length === 0 ? (
                        <div style={{ padding: '10px 14px', color: '#666', fontSize: '14px' }}>No results found</div>
                    ) : (
                        filtered.map(option => (
                            <div
                                key={option.id}
                                onMouseDown={() => handleSelect(option)}
                                style={{ padding: '9px 14px', fontSize: '14px', color: '#e0e0e0', cursor: 'pointer', borderBottom: '1px solid #3a3a3a' }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#383838'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                {renderLabel(option)}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

// ── MyProfile ─────────────────────────────────────────────────────────────────
function MyProfile() {
    const [profile, setProfile] = useState(null);
    const [picklists, setPicklists] = useState(null);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [error, setError] = useState(null);

    // Form state — uses picklist IDs for country/state during editing
    const [form, setForm] = useState({
        display_name: '',
        city: '',
        country_id: '',
        state_id: '',
        member_id: '',
        username: '',
        is_public: true,
    });

    // Username availability: null | 'checking' | 'available' | 'taken' | 'invalid'
    const [usernameStatus, setUsernameStatus] = useState(null);

    useEffect(() => {
        document.title = 'SHARC - My Profile';
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, []);

    useEffect(() => {
        async function loadAll() {
            try {
                const token = await getIdToken();

                const [profileRes, picklistsRes, membersRes] = await Promise.all([
                    axios.get(`${config.API_URL}/user`, { headers: { Authorization: token } }),
                    axios.get(`${config.API_URL}/picklists`),
                    axios.get(`${config.API_URL}/members`),
                ]);

                const p = profileRes.data;
                const pl = picklistsRes.data;
                const memberList = membersRes.data.members || membersRes.data || [];

                setProfile(p);
                setPicklists(pl);
                setMembers(memberList);

                // Resolve stored code strings back to picklist IDs for the form
                const countryObj = p.country
                    ? pl.countries.find(c => c.code === p.country)
                    : null;
                const stateObj = p.state && countryObj
                    ? pl.states_regions.find(s => s.code === p.state && s.country_id === countryObj.id)
                    : null;

                setForm({
                    display_name: p.display_name || '',
                    city: p.city || '',
                    country_id: countryObj ? String(countryObj.id) : '',
                    state_id: stateObj ? String(stateObj.id) : '',
                    member_id: p.member_id ? String(p.member_id) : '',
                    username: p.username || '',
                    is_public: p.is_public !== undefined && p.is_public !== null ? p.is_public : true,
                });

                setLoading(false);
            } catch (e) {
                setError('Failed to load profile.');
                setLoading(false);
            }
        }
        loadAll();
    }, []);

    const handleChange = (field, value) => {
        setForm(prev => ({
            ...prev,
            [field]: value,
            // Reset state when country changes
            ...(field === 'country_id' ? { state_id: '' } : {}),
        }));
        setSaveSuccess(false);
    };

    const filteredStates = picklists && form.country_id
        ? picklists.states_regions.filter(s => s.country_id === parseInt(form.country_id))
        : [];

    // Username availability check (debounced) — mirrors the SHARC ID pattern
    // used on artist/event edit pages, scoped to the users table.
    useEffect(() => {
        if (!profile) return; // wait until we have our own id for exclude_id
        const candidate = form.username.trim().toLowerCase();

        if (!candidate) {
            setUsernameStatus(null);
            return;
        }
        const valid = /^[a-z0-9-]{3,30}$/.test(candidate);
        if (!valid) {
            setUsernameStatus('invalid');
            return;
        }
        setUsernameStatus('checking');
        const timer = setTimeout(() => {
            axios.get(`${config.API_URL}/check-username`, {
                params: { username: candidate, exclude_id: profile.id }
            }).then(res => {
                setUsernameStatus(res.data.available ? 'available' : 'taken');
            }).catch(() => {
                setUsernameStatus(null);
            });
        }, 500);
        return () => clearTimeout(timer);
    }, [form.username, profile]);

    const handleSave = async () => {
        // Block save if the username field is mid-check or has a known conflict
        if (usernameStatus === 'taken' || usernameStatus === 'invalid' || usernameStatus === 'checking') {
            setError(
                usernameStatus === 'checking'
                    ? 'Still checking username availability — try again in a moment.'
                    : 'Please resolve the username issue before saving.'
            );
            return;
        }

        setSaving(true);
        setSaveSuccess(false);
        setError(null);
        try {
            const token = await getIdToken();

            // Resolve IDs back to codes for storage
            const countryObj = form.country_id
                ? picklists.countries.find(c => String(c.id) === form.country_id)
                : null;
            const stateObj = form.state_id
                ? picklists.states_regions.find(s => String(s.id) === form.state_id)
                : null;

            const payload = {
                display_name: form.display_name.trim() || null,
                city: form.city.trim() || null,
                country: countryObj ? countryObj.code : null,
                state: stateObj ? stateObj.code : null,
                member_id: form.member_id ? parseInt(form.member_id) : null,
                username: form.username.trim().toLowerCase() || null,
                is_public: form.is_public,
            };

            const res = await axios.put(`${config.API_URL}/user`, payload, {
                headers: { Authorization: token }
            });

            setProfile(res.data);
            setSaveSuccess(true);
        } catch (e) {
            const serverMessage = e.response && e.response.data && e.response.data.message;
            setError(serverMessage || 'Failed to save profile. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div style={styles.message}>Loading profile...</div>;
    if (error && !profile) return <div style={styles.errorMsg}>{error}</div>;

    const selectedMember = members.find(m => String(m.id) === form.member_id);

    return (
        <div style={styles.page}>
            {/* Nav row */}
            <div style={styles.navRow}>
                <Link to="/" style={styles.backLink}>← Home</Link>
            </div>

            <h1 style={styles.pageTitle}>My Profile</h1>

            <div style={styles.layout}>
                {/* ── Left column: Profile Info ── */}
                <div style={styles.card}>
                    <h2 style={styles.cardTitle}>Profile Information</h2>
                    <p style={styles.cardSubtitle}>This information will appear on your My Shows page.</p>

                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>Display Name</label>
                        <input
                            type="text"
                            value={form.display_name}
                            onChange={e => handleChange('display_name', e.target.value)}
                            placeholder="Your name"
                            style={styles.input}
                        />
                    </div>

                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>City</label>
                        <input
                            type="text"
                            value={form.city}
                            onChange={e => handleChange('city', e.target.value)}
                            placeholder="Your city"
                            style={styles.input}
                        />
                    </div>

                    <div style={styles.fieldRow}>
                        <div style={{ flex: 1 }}>
                            <label style={styles.label}>Country</label>
                            {picklists && (
                                <SearchableSelect
                                    options={picklists.countries}
                                    value={form.country_id}
                                    onChange={val => handleChange('country_id', val)}
                                    placeholder="Select country..."
                                    renderLabel={c => c.name}
                                />
                            )}
                        </div>

                        {filteredStates.length > 0 && (
                            <div style={{ flex: 1 }}>
                                <label style={styles.label}>State / Region</label>
                                <SearchableSelect
                                    options={filteredStates}
                                    value={form.state_id}
                                    onChange={val => handleChange('state_id', val)}
                                    placeholder="Select state..."
                                    renderLabel={s => s.name}
                                />
                            </div>
                        )}
                    </div>

                    {/* Save button */}
                    <div style={styles.saveRow}>
                        {error && <div style={styles.errorMsg}>{error}</div>}
                        {saveSuccess && <div style={styles.successMsg}>Profile saved!</div>}
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            style={saving ? { ...styles.saveBtn, opacity: 0.6 } : styles.saveBtn}
                        >
                            {saving ? 'Saving...' : 'Save Profile'}
                        </button>
                    </div>
                </div>

                {/* ── Right column: Member Claim ── */}
                <div style={styles.card}>
                    <h2 style={styles.cardTitle}>Claim a Member Record</h2>
                    <p style={styles.cardSubtitle}>
                        Are you a performing member of an artist? Link your account to your Member record
                        to track shows you've worked and have them appear in your show history.
                    </p>

                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>Member</label>
                        {members.length > 0 && (
                            <SearchableSelect
                                options={members}
                                value={form.member_id}
                                onChange={val => handleChange('member_id', val)}
                                placeholder="Search members..."
                                renderLabel={m => m.name + (m.hometown_city ? ` — ${m.hometown_city}` : '')}
                            />
                        )}
                    </div>

                    {selectedMember && (
                        <div style={styles.memberPreview}>
                            <div style={styles.memberPreviewName}>{selectedMember.name}</div>
                            {selectedMember.hometown_city && (
                                <div style={styles.memberPreviewSub}>
                                    {[selectedMember.hometown_city, selectedMember.hometown_state].filter(Boolean).join(', ')}
                                </div>
                            )}
                            <Link
                                to={`/members/${selectedMember.slug || selectedMember.id}`}
                                style={styles.memberPreviewLink}
                            >
                                View Member Record →
                            </Link>
                        </div>
                    )}

                    {!selectedMember && (
                        <div style={styles.memberEmpty}>
                            No member linked. Search above to find your record.
                        </div>
                    )}

                    <div style={styles.saveRow}>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            style={saving ? { ...styles.saveBtn, opacity: 0.6 } : styles.saveBtn}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Privacy & Public URL ── */}
            <div style={{ ...styles.card, marginTop: '24px' }}>
                <h2 style={styles.cardTitle}>Privacy & Public URL</h2>
                <p style={styles.cardSubtitle}>
                    Control whether other SHARC users can view your show history, and choose the
                    username that forms your public profile link.
                </p>

                <div style={styles.privacyToggleRow}>
                    <div>
                        <div style={styles.privacyToggleLabel}>Public Profile</div>
                        <div style={styles.privacyToggleHint}>
                            {form.is_public
                                ? 'Other logged-in users can view your show history.'
                                : 'Only you can view your show history.'}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => handleChange('is_public', !form.is_public)}
                        style={{ ...styles.switchTrack, backgroundColor: form.is_public ? '#bb86fc' : '#444' }}
                        aria-pressed={form.is_public}
                    >
                        <span style={{ ...styles.switchKnob, transform: form.is_public ? 'translateX(20px)' : 'translateX(2px)' }} />
                    </button>
                </div>

                <div style={{ ...styles.fieldGroup, marginTop: '20px' }}>
                    <label style={styles.label}>Username</label>
                    <input
                        type="text"
                        value={form.username}
                        onChange={e => handleChange('username', e.target.value.toLowerCase())}
                        placeholder="e.g. andy-slania"
                        style={{
                            ...styles.input,
                            borderColor: usernameStatus === 'taken' || usernameStatus === 'invalid' ? '#cf6679'
                                : usernameStatus === 'available' ? '#4caf7d' : '#444'
                        }}
                    />
                    {usernameStatus === 'checking' && (
                        <div style={styles.sharcChecking}>Checking availability...</div>
                    )}
                    {usernameStatus === 'available' && (
                        <div style={styles.sharcAvailable}>✓ Available — sharc.fm/u/{form.username.trim().toLowerCase()}</div>
                    )}
                    {usernameStatus === 'taken' && (
                        <div style={styles.sharcTaken}>✕ Already taken</div>
                    )}
                    {usernameStatus === 'invalid' && (
                        <div style={styles.sharcTaken}>✕ 3-30 characters: lowercase letters, numbers, and dashes only</div>
                    )}
                    {!usernameStatus && form.is_public && !form.username.trim() && (
                        <div style={styles.privacyToggleHint}>
                            Set a username to make your public profile reachable.
                        </div>
                    )}
                </div>

                <div style={styles.saveRow}>
                    {error && <div style={styles.errorMsg}>{error}</div>}
                    {saveSuccess && <div style={styles.successMsg}>Profile saved!</div>}
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        style={saving ? { ...styles.saveBtn, opacity: 0.6 } : styles.saveBtn}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>

            {/* ── Account Info ── */}
            <div style={{ ...styles.card, marginTop: '24px' }}>
                <h2 style={styles.cardTitle}>Account</h2>
                <div style={styles.accountRow}>
                    <span style={styles.accountLabel}>Member since</span>
                    <span style={styles.accountValue}>
                        {profile.created_at
                            ? new Date(profile.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                            : '—'}
                    </span>
                </div>
                <div style={styles.accountRow}>
                    <span style={styles.accountLabel}>Quick links</span>
                    <Link to="/my-shows" style={styles.quickLink}>My Shows →</Link>
                </div>
            </div>
        </div>
    );
}

const styles = {
    page: { maxWidth: '900px', margin: '0 auto' },
    navRow: { marginBottom: '24px' },
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px' },
    pageTitle: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '32px' },
    layout: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' },
    card: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '12px', padding: '28px' },
    cardTitle: { fontSize: '18px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '6px', marginTop: 0 },
    cardSubtitle: { fontSize: '13px', color: '#777', marginBottom: '24px', lineHeight: '1.6' },
    fieldGroup: { marginBottom: '20px' },
    fieldRow: { display: 'flex', gap: '16px', marginBottom: '20px' },
    label: { display: 'block', fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
    input: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '10px 14px', fontSize: '15px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box', outline: 'none' },
    saveRow: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '16px', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #2a2a2a' },
    saveBtn: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '6px', padding: '10px 28px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' },
    successMsg: { fontSize: '14px', color: '#66bb6a' },
    errorMsg: { fontSize: '14px', color: '#e57373' },
    memberPreview: { backgroundColor: '#2a2040', border: '1px solid #bb86fc44', borderRadius: '8px', padding: '16px', marginTop: '4px' },
    memberPreviewName: { fontSize: '16px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '4px' },
    memberPreviewSub: { fontSize: '13px', color: '#aaa', marginBottom: '10px' },
    memberPreviewLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold' },
    memberEmpty: { fontSize: '13px', color: '#555', fontStyle: 'italic', padding: '12px 0' },
    accountRow: { display: 'flex', alignItems: 'center', gap: '16px', padding: '10px 0', borderBottom: '1px solid #2a2a2a' },
    accountLabel: { fontSize: '13px', color: '#777', minWidth: '140px' },
    accountValue: { fontSize: '14px', color: '#e0e0e0' },
    quickLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px', fontWeight: 'bold' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    privacyToggleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0' },
    privacyToggleLabel: { fontSize: '14px', color: '#e0e0e0', fontWeight: 'bold', marginBottom: '4px' },
    privacyToggleHint: { fontSize: '12px', color: '#888' },
    switchTrack: { width: '44px', height: '24px', borderRadius: '12px', border: 'none', position: 'relative', cursor: 'pointer', flexShrink: 0, padding: 0, transition: 'background-color 0.15s ease' },
    switchKnob: { position: 'absolute', top: '2px', left: 0, width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#f0f0f0', transition: 'transform 0.15s ease', display: 'block' },
    sharcChecking: { fontSize: '13px', color: '#888', marginTop: '6px' },
    sharcAvailable: { fontSize: '13px', color: '#4caf7d', marginTop: '6px' },
    sharcTaken: { fontSize: '13px', color: '#cf6679', marginTop: '6px' },
};

export default MyProfile;
